import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiPlus, FiUsers, FiLoader } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { leadsAPI } from '../services/api';
import theme from '../styles/theme';
import Layout from '../components/Layout';
import { LeadCard, LeadsFiltros, LeadsPaginacao } from '../components/leads';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: ${theme.spacing[4]};
`;

const AddButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  background: ${theme.colors.primary[500]};
  color: ${theme.colors.text.primary};
  border: none;
  border-radius: ${theme.borderRadius.xl};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${theme.colors.primary[600]};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${theme.colors.primary[500]}40;
  }

  &:active {
    transform: translateY(0);
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: ${theme.spacing[6]};
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  color: ${theme.colors.text.muted};
`;

const LoadingSpinner = styled.div`
  font-size: ${theme.fontSizes['2xl']};
  animation: spin 1s linear infinite;
  margin-bottom: ${theme.spacing[4]};

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  font-size: ${theme.fontSizes.base};
  margin: 0;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  text-align: center;
`;

const EmptyIcon = styled.div`
  font-size: ${theme.fontSizes['4xl']};
  color: ${theme.colors.text.muted};
  margin-bottom: ${theme.spacing[4]};
`;

const EmptyTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.xl};
  font-weight: 600;
  margin: 0 0 ${theme.spacing[2]} 0;
`;

const EmptyText = styled.p`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.base};
  margin: 0 0 ${theme.spacing[6]} 0;
`;

const EmptyAction = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  background: ${theme.colors.primary[500]};
  color: ${theme.colors.text.primary};
  border: none;
  border-radius: ${theme.borderRadius.xl};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${theme.colors.primary[600]};
    transform: translateY(-2px);
  }
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  text-align: center;
`;

const ErrorTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.xl};
  font-weight: 600;
  margin: 0 0 ${theme.spacing[2]} 0;
`;

const ErrorText = styled.p`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.base};
  margin: 0 0 ${theme.spacing[6]} 0;
`;

const ErrorAction = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  background: ${theme.colors.error[500]};
  color: ${theme.colors.text.primary};
  border: none;
  border-radius: ${theme.borderRadius.xl};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${theme.colors.error[600]};
  }
`;

const Leads = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [funisList, setFunisList] = useState([]);
  const [origensList, setOrigensList] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
    hasNextPage: false,
    hasPrevPage: false
  });

  const [filtros, setFiltros] = useState({
    search: '',
    funil_id: '',
    origem_id: '',
    empresa: '',
    segmento: '',
    page: 1,
    limit: 10
  });

  // Carregar leads
  const carregarLeads = async (params) => {
    try {
      setLoading(true);
      setError(null);

      const response = await leadsAPI.listar(params);
      
      if (response.data.success) {
        setLeads(response.data.data);
        setPagination(response.data.pagination);
      } else {
        setError('Erro ao carregar leads');
      }
    } catch (err) {
      console.error('Erro ao carregar leads:', err);
      setError('Erro ao conectar com o servidor');
    } finally {
      setLoading(false);
    }
  };

  // Carregar dados auxiliares
  const carregarDadosAuxiliares = async () => {
    try {
      const [funisResponse, origensResponse] = await Promise.all([
        leadsAPI.listarFunis(),
        leadsAPI.listarOrigens()
      ]);

      if (funisResponse.data.success) {
        setFunisList(funisResponse.data.data);
      }
      if (origensResponse.data.success) {
        setOrigensList(origensResponse.data.data);
      }
    } catch (err) {
      console.error('Erro ao carregar dados auxiliares:', err);
    }
  };

  // Carregar leads na inicialização
  useEffect(() => {
    carregarLeads(filtros);
    carregarDadosAuxiliares();
  }, []);

  // Atualizar filtros
  const handleFiltrosChange = (novosFiltros) => {
    setFiltros({
      ...novosFiltros,
      page: 1,
      limit: filtros.limit || 10
    });
  };

  // Buscar com filtros
  const handleBuscar = (param = null) => {
    if (param && typeof param === 'object' && typeof param.preventDefault === 'function') {
      param.preventDefault();
      carregarLeads({ ...filtros, page: 1 });
      return;
    }

    const filtrosParaBusca = param || filtros;
    carregarLeads(filtrosParaBusca);
  };

  // Mudar página
  const handlePageChange = (page) => {
    const novosFiltros = { ...filtros, page };
    setFiltros(novosFiltros);
    carregarLeads(novosFiltros);
  };

  // Mudar registros por página
  const handleItemsPerPageChange = (newLimit) => {
    const novosFiltros = { 
      ...filtros, 
      limit: newLimit,
      page: 1
    };
    setFiltros(novosFiltros);
    carregarLeads(novosFiltros);
  };

  // Navegar para adicionar lead
  const handleAdicionarLead = () => {
    navigate('/leads/novo');
  };

  // Renderizar conteúdo baseado no estado
  const renderContent = () => {
    if (loading) {
      return (
        <LoadingContainer>
          <LoadingSpinner>
            <FiLoader />
          </LoadingSpinner>
          <LoadingText>Carregando leads...</LoadingText>
        </LoadingContainer>
      );
    }

    if (error) {
      return (
        <ErrorState>
          <ErrorTitle>Erro ao carregar leads</ErrorTitle>
          <ErrorText>{error}</ErrorText>
          <ErrorAction onClick={() => carregarLeads(filtros)}>
            Tentar novamente
          </ErrorAction>
        </ErrorState>
      );
    }

    if (leads.length === 0) {
      return (
        <EmptyState>
          <EmptyIcon>
            <FiUsers />
          </EmptyIcon>
          <EmptyTitle>Nenhum lead encontrado</EmptyTitle>
          <EmptyText>
            Não foram encontrados leads com os filtros aplicados.
          </EmptyText>
          <EmptyAction onClick={handleAdicionarLead}>
            <FiPlus />
            Adicionar Primeiro Lead
          </EmptyAction>
        </EmptyState>
      );
    }

    return (
      <>
        <CardsGrid>
          {leads.map((lead) => (
            <LeadCard 
              key={lead.id} 
              lead={lead}
            />
          ))}
        </CardsGrid>

        {pagination.totalPages > 1 && (
          <LeadsPaginacao
            pagination={pagination}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            loading={loading}
          />
        )}
      </>
    );
  };

  return (
    <Layout title="Leads">
      <Container>
        <Header>
          <AddButton onClick={handleAdicionarLead}>
            <FiPlus />
            Adicionar Lead
          </AddButton>
        </Header>

        <Content>
          <LeadsFiltros
            filtros={filtros}
            funisList={funisList}
            origensList={origensList}
            onFiltrosChange={handleFiltrosChange}
            onBuscar={handleBuscar}
            loading={loading}
          />

          {renderContent()}
        </Content>
      </Container>
    </Layout>
  );
};

export default Leads;

